/**
 * Generate a unique device fingerprint based on browser and device characteristics
 * Stores the fingerprint in localStorage to ensure consistency across page loads
 * Uses only stable, deterministic values to ensure consistency across sessions
 * @returns {Promise<string>} A SHA-256 hash representing the device fingerprint
 */
export async function generateDeviceFingerprint() {
  // Check if we already have a fingerprint stored
  const STORAGE_KEY = 'deviceFingerprint_v2'; // Updated version to regenerate existing fingerprints
  const stored = localStorage.getItem(STORAGE_KEY);
  
  if (stored) {
    console.log('Using stored device fingerprint');
    return stored;
  }
  
  console.log('Generating new device fingerprint');
  
  // Collect ONLY stable device characteristics (deterministic across sessions)
  // These values should never change for the same physical device
  const characteristics = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    String(screen.width),
    String(screen.height),
    String(screen.colorDepth),
    String(new Date().getTimezoneOffset()),
    String(navigator.hardwareConcurrency || 'unknown'),
    String(navigator.deviceMemory || 'unknown')
  ];
  
  // Canvas fingerprinting is intentionally excluded because it can vary
  // between sessions due to anti-fingerprinting measures and rendering differences
  
  // Combine all characteristics into a string with consistent formatting
  const fingerprintString = characteristics.join('|');
  
  // Hash the string to create a consistent fingerprint
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprintString);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  // Store in localStorage for future page loads
  localStorage.setItem(STORAGE_KEY, hashHex);
  console.log('Stored device fingerprint:', hashHex.substring(0, 8) + '...');
  
  return hashHex;
}

/**
 * Clear the stored device fingerprint (useful for testing)
 */
export function clearDeviceFingerprint() {
  localStorage.removeItem('deviceFingerprint_v1');
  localStorage.removeItem('deviceFingerprint_v2');
  console.log('Device fingerprint cleared');
}